import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { WordCloudSearch } from '../components/WordCloudSearch'

interface Gallery {
  title: string
  subtitle: string
  category: string
  location: string
  thumbnail: string
  keywords: string[]
  images: Array<{
    id: string
    filename: string
    thumbnail: string
    fullsize: string
    keywords?: string[]
  }>
}

type GalleryData = Record<string, Gallery>

interface SearchResult {
  galleryId: string
  gallery: Gallery
  matchingImages: any[]
}

export const EngagementsPage: React.FC = () => {
  const [galleries, setGalleries] = useState<GalleryData>({})
  const [engagementGalleries, setEngagementGalleries] = useState<[string, Gallery][]>([])
  const [loading, setLoading] = useState(true)
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [isSearchActive, setIsSearchActive] = useState(false)

  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        const response = await fetch('/data/galleries.json')
        const data: GalleryData = await response.json()
        setGalleries(data)
        
        // Filter only engagement galleries
        const engagements = Object.entries(data).filter(([_, gallery]) => 
          gallery.category === 'engagement'
        )
        setEngagementGalleries(engagements)
        
        // Initialize search results with engagement galleries
        const engagementResults: SearchResult[] = engagements.map(([galleryId, gallery]) => ({
          galleryId,
          gallery,
          matchingImages: gallery.images || []
        }))
        setSearchResults(engagementResults)
      } catch (error) {
        console.error('Error fetching galleries:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchGalleries()
  }, [])

  const handleSearchResults = (results: SearchResult[]) => {
    // Filter search results to only show engagement galleries
    const engagementResults = results.filter(result => result.gallery.category === 'engagement')
    setSearchResults(engagementResults)
    setIsSearchActive(engagementResults.length !== engagementGalleries.length)
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6 tracking-wide">
            Engagement Photography
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Capturing the joy, excitement, and intimate moments of couples 
            as they begin their journey together. From urban San Francisco 
            settings to scenic coastal locations.
          </p>
        </div>
      </section>

      {/* Word Cloud Search */}
      <WordCloudSearch 
        galleries={Object.fromEntries(engagementGalleries)} 
        onSearchResults={handleSearchResults} 
      />

      {/* Gallery Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {isSearchActive && (
            <div className="text-center mb-8">
              <h2 className="text-2xl font-light text-gray-900 mb-2">
                Search Results ({searchResults.length} {searchResults.length === 1 ? 'gallery' : 'galleries'})
              </h2>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {searchResults.map(({ galleryId, gallery }) => (
              <GalleryCard key={galleryId} galleryId={galleryId} gallery={gallery} />
            ))}
          </div>
          
          {searchResults.length === 0 && isSearchActive && (
            <div className="text-center py-12">
              <p className="text-gray-600">
                No engagement galleries found matching your search criteria.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

interface GalleryCardProps {
  galleryId: string
  gallery: Gallery
}

const GalleryCard: React.FC<GalleryCardProps> = ({ galleryId, gallery }) => {
  return (
    <Link to={`/gallery/${galleryId}`} className="group block">
      <div className="aspect-[4/5] overflow-hidden bg-gray-100 mb-4 rounded-sm">
        <img
          src={gallery.thumbnail}
          alt={gallery.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
          loading="lazy"
        />
      </div>
      <div className="space-y-1">
        <h3 className="text-lg font-light text-gray-900 group-hover:text-gray-700 transition-colors duration-200">
          {gallery.title}
        </h3>
        <p className="text-base text-gray-600">{gallery.subtitle}</p>
        <p className="text-sm text-gray-500 uppercase tracking-wide">
          {gallery.location}
        </p>
        {/* Keywords preview */}
        <div className="flex flex-wrap gap-1 mt-2">
          {gallery.keywords?.slice(0, 3).map((keyword, index) => (
            <span key={index} className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
              {keyword}
            </span>
          ))}
          {gallery.keywords && gallery.keywords.length > 3 && (
            <span className="text-xs text-gray-400">+{gallery.keywords.length - 3}</span>
          )}
        </div>
      </div>
    </Link>
  )
}
